import React from 'react';

import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Box } from '@atlaskit/primitives/compiled';
import DefaultAppIcon from '@atlaskit/icon/core/app';

import { FallbackableImage } from '@confluence/fallbackable-image';

const styles = cssMap({
	wrapper: {
		display: 'flex',
		alignItems: 'center',
		flexShrink: 0,
	},
	iconSlot: {
		borderRadius: token('radius.small'),
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		marginInlineStart: token('space.negative.100'),
		position: 'relative',
	},
	iconSlotFirst: {
		borderRadius: token('radius.small'),
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		marginInlineStart: '0',
		position: 'relative',
	},
});

const ICON_SIZE: Record<'small' | 'large', number> = {
	small: 16,
	large: 28,
};

const TILT_ANGLE_DEG = 10;

const iconZIndex = (index: number, total: number) => total - index;

function getTiltTransform(index: number): string {
	const rotationDeg = index % 2 === 0 ? -TILT_ANGLE_DEG : TILT_ANGLE_DEG;
	return `rotate(${rotationDeg}deg)`;
}

type StackedContentTypeIconsProps = {
	iconUrls: string[];
	variant?: 'small' | 'large';
	tilted?: boolean;
	testId?: string;
};

export const StackedContentTypeIcons = ({
	iconUrls,
	variant = 'small',
	tilted = false,
	testId = 'stacked-content-type-icons',
}: StackedContentTypeIconsProps) => {
	const displayUrls = (iconUrls ?? []).slice(0, 3);

	return (
		<Box xcss={styles.wrapper} testId={testId}>
			{displayUrls.length > 0 ? (
				displayUrls.map((url, i) => (
					<Box
						key={`${url}-${i}`}
						xcss={i === 0 ? styles.iconSlotFirst : styles.iconSlot}
						style={{
							zIndex: iconZIndex(i, displayUrls.length),
							transform: tilted && displayUrls.length > 1 ? getTiltTransform(i) : undefined,
						}}
						aria-hidden="true"
					>
						<FallbackableImage
							src={url}
							width={ICON_SIZE[variant]}
							height={ICON_SIZE[variant]}
							fallback={() => <DefaultAppIcon label="" />}
							role="presentation"
						/>
					</Box>
				))
			) : (
				<DefaultAppIcon label="" color="currentColor" />
			)}
		</Box>
	);
};
